import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, ClipboardCheck, Pencil, Star, Trash2, Users } from 'lucide-react';
import { CourtierValidationDto, OperationResponse, TunnelValideurDto } from 'api/funnel';
import { Sizes } from 'enums/sizes';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { usePanel } from 'hooks/usePanel';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { SwitchComponent } from 'components/forms/Switch';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { useFunnelDetail } from '../FunnelDetailContext';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { BlockCard, StripedList } from 'back-office/shared/components/BlockCard';
import { AddValidatorModal } from '../forms/AddValidatorModal';
import { ValidatorScopeModal } from '../forms/ValidatorScopeModal';
import { ValidatorVariablesPanel } from '../forms/ValidatorVariablesPanel';
import { DASH } from '../funnelFormat';

// Darker shade of the theme colour, for text on light primary backgrounds.
const PRIMARY_DARK_TEXT = 'text-[color-mix(in_srgb,var(--primary-color)_75%,black)]';
const BADGE = 'max-w-full truncate rounded-full px-2.5 py-0.5 text-xs font-bold';
const PLATFORM_BADGE = `${BADGE} bg-primary/[0.12] ${PRIMARY_DARK_TEXT}`;
const NATURE_BADGE =
  'rounded-md bg-zinc-200 px-[7px] py-0.5 text-[11px] font-bold tracking-[0.04em] text-zinc-700';
const TEXT_LINK = 'text-[13px] font-semibold text-primary hover:text-primary/80 disabled:opacity-40';
const OUTLINE_BUTTON =
  'inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-zinc-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const COLUMN_HEADER = 'text-[11px] font-bold uppercase tracking-[0.06em] text-gray-600';
const HEADER_ROW = 'grid h-8 items-center gap-x-4 border-y border-gray-100 bg-gray-50 px-5';

// Column templates shared by each grid's header row and its data rows.
const VALIDATOR_COLUMNS = 'grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_140px_150px_170px_112px]';
const BROKER_COLUMNS = 'grid-cols-[minmax(0,1.3fr)_200px_minmax(0,1.4fr)_216px]';

/** A broker authorised through several platforms appears once per authorisation. */
const brokerRowKey = (courtier: CourtierValidationDto) =>
  `${courtier.courtierId}:${courtier.plateformeId}`;

const scopeLabel = (valideur: TunnelValideurDto) =>
  valideur.courtierIds.length === 0
    ? 'Tous les courtiers'
    : `${valideur.courtierIds.length} ${valideur.courtierIds.length > 1 ? 'courtiers' : 'courtier'}`;

const SubsectionTitle: FC<{ title: string; count?: number; children?: ReactNode }> = ({
  title,
  count,
  children,
}) => (
  <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-3 px-5 py-2">
    <div className="flex items-center gap-2">
      <h3 className="text-[15px] font-bold text-zinc-800">{title}</h3>
      {count !== undefined && (
        <span
          className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-primary/[0.12] px-[7px] text-xs font-bold ${PRIMARY_DARK_TEXT}`}
        >
          {count}
        </span>
      )}
    </div>
    {children}
  </div>
);

/** Bordered neutral icon button of a validator row; the icon carries the colour. */
const IconAction: FC<{
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}> = ({ label, icon, onClick, disabled }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-zinc-200 bg-white transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
  >
    {icon}
  </button>
);

/**
 * « Validation des devis » block: whether quotes of this tunnel must be validated, by
 * whom, and for which authorised brokers.
 */
export const ValidationBlock: FC = () => {
  const { tunnelId } = useFunnelDetail();
  const { openBlankDialog } = useDialog();
  const { openPanel } = usePanel();
  const { createMenuItem } = useActionMenu();
  const [isSaving, setIsSaving] = useState(false);
  const [toRemove, setToRemove] = useState<TunnelValideurDto | null>(null);
  // Target value of the bulk action awaiting confirmation (true = optional for all).
  const [bulkOptional, setBulkOptional] = useState<boolean | null>(null);

  const fetchConfig = useCallback(
    () => funnelsAPI.getFunnelValidationConfig({ tunnelId }),
    [tunnelId]
  );
  const { data, isLoading, error, reload } = useAsyncResource(fetchConfig);

  const valideurs = useMemo(() => data?.valideurs ?? [], [data]);
  const courtiers = useMemo(() => data?.courtiers ?? [], [data]);
  const variables = useMemo(() => data?.variables ?? [], [data]);
  const openVariableCount = variables.filter((variable) => variable.modifiableParValideur).length;

  const valideurNames = useMemo(
    () => new Map(valideurs.map((valideur) => [valideur.id, valideur.nom ?? DASH])),
    [valideurs]
  );

  // The detail DTO does not expose the tunnel platforms: the platforms through which
  // brokers are authorised on the tunnel are the closest known approximation.
  const tunnelPlateformeIds = useMemo(
    () => Array.from(new Set(courtiers.map((courtier) => courtier.plateformeId))),
    [courtiers]
  );

  /** Runs a mutation, reports its outcome and reloads the configuration. */
  const mutate = async (
    call: () => Promise<OperationResponse>,
    successMessage: string,
    failureMessage: string
  ) => {
    setIsSaving(true);
    try {
      const response = await call();
      if (response.isSuccess) {
        toast.success(successMessage);
        await reload();
      } else {
        toast.error(response.errorMessage || failureMessage);
      }
    } catch (err) {
      console.error(failureMessage, err);
      const parsed = await parseApiError(err);
      toast.error(parsed.message || failureMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const setValidationRequired = (validationRequise: boolean) =>
    mutate(
      () =>
        funnelsAPI.setFunnelValidationRequired({
          tunnelId,
          setValidationRequiseCommand: { validationRequise },
        }),
      validationRequise ? 'Validation activée' : 'Validation désactivée',
      "La modification de la validation a échoué"
    );

  const setMainValidator = (valideur: TunnelValideurDto) =>
    mutate(
      () => funnelsAPI.setFunnelMainValidator({ tunnelId, valideurId: valideur.id }),
      'Valideur principal désigné',
      'La désignation du valideur principal a échoué'
    );

  const removeValidator = async () => {
    if (!toRemove) return;
    const valideurId = toRemove.id;
    await mutate(
      () => funnelsAPI.removeFunnelValidator({ tunnelId, valideurId }),
      'Valideur retiré',
      'Le retrait du valideur a échoué'
    );
    setToRemove(null);
  };

  const setBrokerOptional = (courtier: CourtierValidationDto, validationOptionnelle: boolean) => {
    if (courtier.validationOptionnelle === validationOptionnelle) return;
    return mutate(
      () =>
        funnelsAPI.setFunnelBrokerOptionalValidation({
          tunnelId,
          courtierId: courtier.courtierId,
          setCourtierValidationOptionnelleCommand: { validationOptionnelle },
        }),
      'Validation du courtier modifiée',
      'La modification a échoué'
    );
  };

  const applyBulk = async () => {
    if (bulkOptional === null) return;
    const validationOptionnelle = bulkOptional;
    await mutate(
      () =>
        funnelsAPI.setFunnelOptionalValidationForAll({
          tunnelId,
          setValidationOptionnellePourTousCommand: { validationOptionnelle },
        }),
      validationOptionnelle
        ? 'Validation optionnelle pour tous les courtiers'
        : 'Validation obligatoire pour tous les courtiers',
      'La modification a échoué'
    );
    setBulkOptional(null);
  };

  const openAddValidator = () =>
    openBlankDialog(
      { size: Sizes['2xl'] },
      <AddValidatorModal
        tunnelId={tunnelId}
        tunnelPlateformeIds={tunnelPlateformeIds}
        onSuccess={reload}
      />
    );

  const openScope = (valideur: TunnelValideurDto) =>
    openBlankDialog(
      { size: Sizes['2xl'] },
      <ValidatorScopeModal
        tunnelId={tunnelId}
        valideur={valideur}
        courtiers={courtiers}
        onSuccess={reload}
      />
    );

  const openValidatorVariables = () =>
    openPanel(
      { title: 'Variables ouvertes au valideur', size: Sizes['2xl'] },
      <ValidatorVariablesPanel tunnelId={tunnelId} variables={variables} onSuccess={reload} />
    );

  const validationRequise = data?.validationRequise ?? false;

  return (
    <>
      <BlockCard
        title="Validation des devis"
        description="Qui valide les devis de ce tunnel, et pour quels courtiers."
        icon={<ClipboardCheck className="h-4 w-4" />}
        actions={
          validationRequise
            ? [createMenuItem('add', 'Ajouter un valideur', openAddValidator, { icon: 'add' })]
            : undefined
        }
        isLoading={isLoading}
        error={error}
        flushBody
      >
        <div className="flex flex-col gap-3 px-5 py-4">
          <SwitchComponent
            name="validationRequise"
            label="Ce tunnel nécessite une validation"
            checked={validationRequise}
            disabled={isSaving}
            onChange={setValidationRequired}
          />

          {!validationRequise && (
            <p className="text-sm italic text-gray-400">
              La validation est désactivée sur ce tunnel.
            </p>
          )}

          {validationRequise && data && !data.estComplete && (
            <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              Désignez au moins un valideur : sans valideur principal, aucune demande de
              validation ne pourra être créée.
            </p>
          )}
        </div>

        {validationRequise && (
          <>
            <section className="border-t border-gray-100 pb-2">
              <SubsectionTitle title="Valideurs" count={valideurs.length} />
              {valideurs.length === 0 ? (
                <p className="px-5 pb-2 text-sm text-gray-400">Aucun valideur sur ce tunnel.</p>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[52rem]">
                    <div className={`${HEADER_ROW} ${VALIDATOR_COLUMNS}`}>
                      <span className={COLUMN_HEADER}>Valideur</span>
                      <span className={COLUMN_HEADER}>Cabinet</span>
                      <span className={COLUMN_HEADER}>Plateforme</span>
                      <span className={COLUMN_HEADER}>Rôle</span>
                      <span className={COLUMN_HEADER}>Périmètre</span>
                      <span className={`${COLUMN_HEADER} text-right`}>Actions</span>
                    </div>
                    <StripedList>
                      {valideurs.map((valideur) => (
                        <li
                          key={valideur.id}
                          className={`grid min-h-[52px] items-center gap-x-4 px-5 py-1.5 ${VALIDATOR_COLUMNS}`}
                        >
                          <div className="flex min-w-0 flex-col gap-px">
                            <span
                              className={`truncate text-sm font-bold ${
                                valideur.briqueActive
                                  ? 'text-zinc-800'
                                  : 'text-gray-400 line-through'
                              }`}
                            >
                              {valideur.nom ?? DASH}
                            </span>
                            <span className="truncate text-xs text-gray-500">
                              {valideur.email ?? DASH}
                            </span>
                            {!valideur.briqueActive && (
                              <span className="truncate text-xs font-medium text-red-600">
                                n'a plus la brique Valideur
                              </span>
                            )}
                          </div>

                          <span className="min-w-0 truncate text-sm text-zinc-700">
                            {valideur.cabinet ?? DASH}
                          </span>

                          <div className="flex min-w-0">
                            <span className={PLATFORM_BADGE}>{valideur.plateformeNom ?? DASH}</span>
                          </div>

                          <div className="flex min-w-0">
                            {valideur.isPrincipal ? (
                              <span className="inline-flex items-center gap-[5px] rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-bold text-green-800">
                                <Star className="h-3 w-3 fill-current" />
                                Principal
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setMainValidator(valideur)}
                                disabled={isSaving}
                                className={TEXT_LINK}
                              >
                                Définir principal
                              </button>
                            )}
                          </div>

                          <div className="flex min-w-0">
                            <button
                              type="button"
                              onClick={() => openScope(valideur)}
                              className={`${TEXT_LINK} truncate underline decoration-dotted underline-offset-[3px]`}
                            >
                              {scopeLabel(valideur)}
                            </button>
                          </div>

                          <div className="flex justify-end gap-1.5">
                            <IconAction
                              label="Périmètre"
                              icon={<Users className="h-[15px] w-[15px] text-primary" />}
                              onClick={() => openScope(valideur)}
                            />
                            {valideur.isPrincipal ? (
                              // Empty slot keeps the remove button aligned across rows.
                              <span className="h-[30px] w-[30px]" aria-hidden="true" />
                            ) : (
                              <IconAction
                                label="Définir principal"
                                icon={<Star className="h-[15px] w-[15px] text-amber-700" />}
                                onClick={() => setMainValidator(valideur)}
                                disabled={isSaving}
                              />
                            )}
                            <IconAction
                              label="Retirer"
                              icon={<Trash2 className="h-[15px] w-[15px] text-red-700" />}
                              onClick={() => setToRemove(valideur)}
                            />
                          </div>
                        </li>
                      ))}
                    </StripedList>
                  </div>
                </div>
              )}
            </section>

            <section className="border-t border-gray-100 pb-2">
              <SubsectionTitle title="Courtiers habilités" count={courtiers.length}>
                {courtiers.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setBulkOptional(false)}
                      disabled={isSaving}
                      className={OUTLINE_BUTTON}
                    >
                      Rendre obligatoire pour tous
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkOptional(true)}
                      disabled={isSaving}
                      className={OUTLINE_BUTTON}
                    >
                      Rendre optionnelle pour tous
                    </button>
                  </div>
                )}
              </SubsectionTitle>
              {courtiers.length === 0 ? (
                <p className="px-5 pb-2 text-sm text-gray-400">
                  Aucun courtier n'est habilité à ce tunnel.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[44rem]">
                    <div className={`${HEADER_ROW} ${BROKER_COLUMNS}`}>
                      <span className={COLUMN_HEADER}>Courtier</span>
                      <span className={COLUMN_HEADER}>Plateforme · nature</span>
                      <span className={COLUMN_HEADER}>Valideurs</span>
                      <span className={`${COLUMN_HEADER} text-right`}>Validation</span>
                    </div>
                    <StripedList>
                      {courtiers.map((courtier) => (
                        <li
                          key={brokerRowKey(courtier)}
                          className={`grid min-h-[46px] items-center gap-x-4 px-5 py-1.5 ${BROKER_COLUMNS}`}
                        >
                          <span className="min-w-0 truncate text-sm font-bold text-zinc-800">
                            {courtier.nom ?? DASH}
                          </span>

                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className={PLATFORM_BADGE}>
                              {courtier.plateformeNom ?? DASH}
                            </span>
                            <span className={NATURE_BADGE}>{courtier.nature ?? DASH}</span>
                          </div>

                          <span className="min-w-0 truncate text-[13px] text-zinc-700">
                            {courtier.repliPrincipal ? (
                              <span className="italic text-gray-400">principal</span>
                            ) : (
                              courtier.valideurIds
                                .map((id) => valideurNames.get(id) ?? DASH)
                                .join(', ')
                            )}
                          </span>

                          <div className="flex justify-end">
                            <div
                              role="group"
                              aria-label="Validation"
                              className="inline-flex overflow-hidden rounded-lg border border-zinc-300 bg-white"
                            >
                              {[
                                { optional: false, label: 'Obligatoire' },
                                { optional: true, label: 'Optionnelle' },
                              ].map((option) => {
                                const active = courtier.validationOptionnelle === option.optional;
                                return (
                                  <button
                                    key={option.label}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => setBrokerOptional(courtier, option.optional)}
                                    disabled={isSaving}
                                    className={`px-3 py-[5px] text-xs transition-colors border-l border-zinc-300 first:border-l-0 disabled:opacity-60 ${
                                      active
                                        ? 'bg-primary font-bold text-white'
                                        : 'font-semibold text-zinc-600 hover:bg-primary/10'
                                    }`}
                                  >
                                    {option.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </li>
                      ))}
                    </StripedList>
                  </div>
                </div>
              )}
            </section>

            <section className="flex min-h-[64px] flex-wrap items-center justify-between gap-4 border-t border-gray-100 px-5 py-3">
              <div className="flex min-w-0 flex-wrap items-center gap-4">
                <h3 className="text-[15px] font-bold text-zinc-800">
                  Variables ouvertes au valideur
                </h3>
                <div className="flex min-w-0 items-center gap-2.5">
                  {variables.length > 0 && (
                    <div className="flex flex-wrap gap-[3px]" aria-hidden="true">
                      {/* The first n squares are filled: the gauge shows a count, not positions. */}
                      {variables.map((variable, index) => (
                        <span
                          key={variable.variableValueId}
                          className={`h-2.5 w-2.5 rounded-[3px] ${
                            index < openVariableCount ? 'bg-primary' : 'bg-primary/[0.16]'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                  <span className="text-sm text-zinc-700">
                    {variables.length === 0 ? (
                      'Aucune variable sur ce tunnel.'
                    ) : openVariableCount === 0 ? (
                      'Aucune variable ouverte au valideur.'
                    ) : (
                      <>
                        <strong className="font-bold text-zinc-800">
                          {openVariableCount}{' '}
                          {openVariableCount > 1 ? 'variables ouvertes' : 'variable ouverte'}
                        </strong>{' '}
                        au valideur sur {variables.length}
                      </>
                    )}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={openValidatorVariables}
                disabled={variables.length === 0}
                className={OUTLINE_BUTTON}
              >
                <Pencil className="h-3.5 w-3.5" />
                Modifier
              </button>
            </section>
          </>
        )}
      </BlockCard>

      <ConfirmationOverlay
        isOpen={Boolean(toRemove)}
        onClose={() => setToRemove(null)}
        onConfirm={removeValidator}
        isLoading={isSaving}
        title="Retirer ce valideur ?"
        description={`${toRemove?.nom ?? 'Ce collaborateur'} ne validera plus les devis de ce tunnel.`}
        confirmLabel="Retirer"
      />

      <ConfirmationOverlay
        isOpen={bulkOptional !== null}
        onClose={() => setBulkOptional(null)}
        onConfirm={applyBulk}
        isLoading={isSaving}
        title={
          bulkOptional
            ? 'Rendre la validation optionnelle pour tous ?'
            : 'Rendre la validation obligatoire pour tous ?'
        }
        description={
          bulkOptional
            ? 'Tous les courtiers habilités pourront se passer de la validation.'
            : 'Tous les courtiers habilités devront faire valider leurs devis.'
        }
        confirmLabel="Confirmer"
      />
    </>
  );
};
